/**
 * Poll a backend voice-transcription job until it completes.
 *
 * The POST endpoints (/api/voice/transcribe/…) now return 202 with
 * {job_id, status: 'processing', status_url} because transcription runs
 * in a Celery worker. Callers pass the status_url here and get back the
 * final result payload ({text, duration, language, summary?, action_items?}).
 */

export interface TranscriptionResult {
  text?: string;
  duration?: number;
  language?: string;
  summary?: string;
  action_items?: string[];
  [key: string]: unknown;
}

export interface PollOptions {
  intervalMs?: number;
  maxAttempts?: number;
}

export async function pollTranscriptionStatus(
  apiBase: string,
  statusUrl: string,
  options: PollOptions = {}
): Promise<TranscriptionResult> {
  // ~120s ceiling (matches gunicorn timeout + Celery slack). Worker runs with
  // a 300s soft_time_limit so results should arrive well before this.
  const intervalMs = options.intervalMs ?? 1500;
  const maxAttempts = options.maxAttempts ?? 80;
  const url = statusUrl.startsWith('http') ? statusUrl : `${apiBase}${statusUrl}`;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));

    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}));
      throw new Error(errData.error || `Transcription status check failed (HTTP ${resp.status})`);
    }

    const payload = await resp.json();
    const status = payload?.status;

    if (status === 'complete') {
      return payload as TranscriptionResult;
    }
    if (status === 'error') {
      throw new Error(payload.error || 'Transcription failed');
    }
    // status === 'processing' → keep polling
  }

  throw new Error('Transcription timed out. Please try again with a shorter recording.');
}
